import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  Badge,
  ProgressBar,
  Button,
  Spinner
} from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import { currentFynencialYear } from '../../../../../../../services/commonServices';

const API_BASE_URL = "http://13.205.22.175";
const REVIEW_QUEUE_URL = "/api/admin/tdsReviewqueue";
const API_TOKEN = "7fa5d115dbbf825a0a4abb2b2a7f37de";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    Authorization: `Bearer ${API_TOKEN}`,
    "Content-Type": "application/json",
  },
});

const Hra = () => {
  const [loading, setLoading] = useState(false);
  const [apiData, setApiData] = useState(null);
  const [apiError, setApiError] = useState(null);

  // Get User Context from LocalStorage
  const storedUser = JSON.parse(localStorage.getItem("tdsUser") || "{}");
  const staffId = storedUser.staffId || storedUser.id || storedUser.employeeId;

  // Get user info from localStorage
  const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
  const userId = userData.user_id || userData.id || "123";

  const financialYear = storedUser.financialYear || userData.financialYear || currentFynencialYear();
  const authToken = localStorage.getItem("token") || userData.token || userData.access_token;
  const requestHeaders = {
    "Content-Type": "application/json",
    ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
  };

  const fetchHRAData = async () => {
    // Re-read localStorage in case selection changed elsewhere
    const storedUserLocal = JSON.parse(localStorage.getItem("tdsUser") || "{}");
    const userDataLocal = JSON.parse(localStorage.getItem("user_data") || "{}");
    const staffIdLocal = storedUserLocal.staffId || storedUserLocal.id || storedUserLocal.employeeId;
    const userIdLocal = userDataLocal.user_id || userDataLocal.id || userId;
    const financialYearLocal = storedUserLocal.financialYear || userDataLocal.financialYear || financialYear;
    const authTokenLocal = localStorage.getItem("token") || userDataLocal.token || userDataLocal.access_token;
    const requestHeadersLocal = {
      "Content-Type": "application/json",
      ...(authTokenLocal ? { Authorization: `Bearer ${authTokenLocal}` } : {}),
    };

    if (!staffIdLocal) {
      console.log("No staffId found for HRA data (local)");
      return;
    }

    setLoading(true);
    setApiError(null);

    try {
      const payload = {
        userId: userIdLocal,
        staffId: Number(staffIdLocal),
        financialYear: financialYearLocal,
      };
      console.debug("HRA fetch payload:", payload);

      const response = await api.post(
        REVIEW_QUEUE_URL,
        payload,
        {
          headers: requestHeadersLocal,
        }
      );

      console.debug("HRA fetch response:", response);
      const body = response?.data;
      // Accept either code===200 or status===true depending on backend
      if (body && (body.code === 200 || body.status === true) && body.data) {
        setApiData(body.data);
        setApiError(null);
      } else {
        const msg = body?.message || `Unexpected API response: ${JSON.stringify(body)}`;
        throw new Error(msg);
      }
    } catch (error) {
      console.error("HRA Load Error:", error);
      setApiError(error);
      toast.error(error.message || "Failed to load HRA data");
    } finally {
      setLoading(false);
    }
  };

  // 3. Fetch Data on Mount
  useEffect(() => {
    fetchHRAData();
  }, [staffId, userId]);

  // 4. Loading State
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center h-100">
        <Spinner animation="border" variant="primary" />
        <span className="ms-3 text-white">Loading HRA Data...</span>
      </div>
    );
  }

  // 5. Error State
  if (apiError) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-8">
            <div className="card bg-dark border-danger">
              <div className="card-header bg-danger text-white">
                <h4 className="mb-0">Error Loading HRA Data</h4>
              </div>
              <div className="card-body">
                <p className="text-white">{apiError.message}</p>
                <p className="text-gray small">
                  API Endpoint: {REVIEW_QUEUE_URL}
                </p>
                <button 
                  className="btn btn-primary mt-3"
                  onClick={() => {
                    setApiError(null);
                    fetchHRAData();
                  }}
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No staffId provided
  if (!staffId) {
    return (
      <div className="text-center py-5">
        <div className="card bg-dark border-warning" style={{ maxWidth: '500px', margin: '0 auto' }}>
          <div className="card-header bg-warning text-dark">
            <h4 className="mb-0">No Employee Selected</h4>
          </div>
          <div className="card-body">
            <p className="color-gray">
              Please select an employee from the TDS employee list to view HRA details.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // No API data
  if (!apiData) {
    return (
      <div className="text-center py-5">
        <div className="card bg-dark border-info" style={{ maxWidth: '500px', margin: '0 auto' }}>
          <div className="card-header bg-info text-white">
            <h4 className="mb-0">No HRA Data Available</h4>
          </div>
          <div className="card-body">
            <p className="color-gray">
              Unable to load HRA data for the selected employee.
            </p>
            <div className="mt-3">
              <p className="small color-gray">
                Employee ID: <strong>{staffId}</strong><br />
                Financial Year: <strong>{currentFynencialYear()}</strong>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 6. Safe Data Extraction
  const employeeInfo = apiData.employeeInfo || {};
  const sections = apiData.sections || {};
  const overallStatus = apiData.overallStatus || {};
  const displayFinancialYear = employeeInfo.financialYear || currentFynencialYear();

  // Extract HRA section data
  const hraSection = sections.hra || {};
  const hraFields = hraSection.fields || {};
  const rentDetails = hraSection.rentDetails || {};
  const landlordDetails = hraSection.landlordDetails || {};
  const hraDocuments = hraSection.documents || {};
  const rentReceipts = Array.isArray(hraDocuments.rentReceipts) ? hraDocuments.rentReceipts : [];
  const reviewActions = hraSection.actions || {};

  const executeReviewAction = async (action, additionalData = {}) => {
    if (!staffId || !userId) {
      toast.warning("Missing user or staff information for HRA action.");
      return;
    }

    try {
      setLoading(true);
      const response = await api.post(
        REVIEW_QUEUE_URL,
        {
          userId,
          staffId: Number(staffId),
          financialYear,
          action,
          section: "hra",
          ...additionalData,
        },
        {
          headers: requestHeaders,
        }
      );

      const body = response?.data;
      if (body && body.code === 200) {
        toast.success(`Action ${action} completed successfully.`);
        await fetchHRAData();
      } else {
        throw new Error(body?.message || "Action failed");
      }
    } catch (error) {
      console.error(`HRA action error (${action}):`, error);
      toast.error(error.message || `Failed to ${action.replace("_", " ")}`);
    } finally {
      setLoading(false);
    }
  };

  // Map API Values to UI Variables
  const actualHra = parseInt(hraFields.actualHRA?.value ?? hraFields.actualHRAReceived?.value) || 0;
  const basicSalary = parseInt(hraFields.basicSalary?.value) || 0;
  const rentPaid = parseInt(hraFields.annualRent?.value ?? rentDetails.totalRentPaid ?? hraFields.rentPaid?.value) || 0;
  const annualHRAExemption = parseInt(hraFields.annualExemption?.value ?? hraFields.annualHRAExemption?.value) || 0;
  const monthlyRent = parseInt(rentDetails.monthlyRentAmount) || 0;
  const totalRentPaid = parseInt(rentDetails.totalRentPaid) || rentPaid;
  const cityCategory = hraFields.cityCategory?.value || "Metro";
  const isMetro = cityCategory === "Metro";
  const basicSalaryPercent = parseInt(hraFields.fiftyPercentSalary?.value) || Math.round(basicSalary * (isMetro ? 0.5 : 0.4));
  const tenPercentBasic = parseInt(hraFields.rentMinus10Percent?.value) || Math.round(basicSalary * 0.1);
  const rentMinus10Percent = parseInt(hraFields.rentMinus10Percent?.value) || Math.max(0, rentPaid - tenPercentBasic);
  const fiftyPercentSalary = parseInt(hraFields.fiftyPercentSalary?.value) || basicSalaryPercent;
  const leastOfThree = parseInt(hraFields.leastOfThree?.value) || Math.min(actualHra, rentMinus10Percent, fiftyPercentSalary);
  const finalExemption = annualHRAExemption || leastOfThree;
  const monthlyExemption = parseInt(hraFields.monthlyExemption?.value) || Math.round(finalExemption / 12);
  const hasRentReceipts = rentReceipts.length > 0;
  const hasLandlordPAN = Boolean(landlordDetails.pan) || landlordDetails.panRequired === false;

  // Get uploaded rent receipt URL
  const rentReceiptUrl = rentReceipts[0];

  return (
    <div className="p-4" style={{ height: 'calc(100% - 100px)', overflowY: 'auto' }}>
      {/* Header */}
      <Row className="mb-4 align-items-center">
        <Col>
          <h4 className="text-white mb-1">
            {employeeInfo.employeeName || storedUser.employeeName || "Employee"} · FY {displayFinancialYear}
          </h4>
          <div className="mt-2">
            <Badge bg="primary" className="me-2">PAN: {employeeInfo.panNumber || employeeInfo.employeePAN || "N/A"}</Badge>
            <Badge bg="info" className="me-2">{cityCategory} City</Badge>
            <Badge bg={(hraSection.reviewStatus?.statusText || hraSection.status) === 'Approved' ? 'success' : (hraSection.reviewStatus?.statusText || hraSection.status) === 'Rejected' ? 'danger' : 'warning'} className="text-white">
              {(hraSection.reviewStatus?.statusText || hraSection.status || 'Pending').toUpperCase()}
            </Badge>
          </div>
        </Col>
      </Row>

      <Row>
        {/* Left Column */}
        <Col lg={8}>
          {/* HRA Calculation */}
          <Card className="bg-dark text-light border-secondary mb-4">
            <Card.Header className="bg-purple text-white">
              <h5 className="mb-0">HRA Exemption Review</h5>
            </Card.Header>
            <Card.Body>
              <p className="text-muted mb-4">
                Review and verify House Rent Allowance exemption as per Section 10(13A)
              </p>

              <Row className="g-4">
                <Col md={6}>
                  <div className="d-flex justify-content-between py-2 border-bottom border-secondary">
                    <span>Actual HRA Received (annual)</span>
                    <strong>₹{actualHra.toLocaleString('en-IN')}</strong>
                  </div>
                  <div className="d-flex justify-content-between py-2 border-bottom border-secondary">
                    <span>Rent Paid - 10% of Basic</span>
                    <strong>₹{rentMinus10Percent.toLocaleString('en-IN')}</strong>
                  </div>
                  <div className="d-flex justify-content-between py-2 border-bottom border-secondary">
                    <span>{isMetro ? "50%" : "40%"} of Basic ({isMetro ? "Metro" : "Non-Metro"})</span>
                    <strong>₹{fiftyPercentSalary.toLocaleString('en-IN')}</strong>
                  </div>
                  <div className="d-flex justify-content-between py-2 border-bottom border-secondary">
                    <span>Least of Three (Exemption)</span>
                    <strong className="text-success">₹{leastOfThree.toLocaleString('en-IN')}</strong>
                  </div>
                </Col>

                <Col md={6}>
                  <Card className="bg-gradient text-white border-0 text-center" style={{ background: "linear-gradient(135deg, #6a11cb, #2575fc)" }}>
                    <Card.Body className="py-5">
                      <h6 className="mb-3">Approved Exemption</h6>
                      <h2 className="display-5 fw-bold mb-3">₹{finalExemption.toLocaleString('en-IN')}</h2>
                      <p className="mb-2 fs-5">Annual HRA Exemption</p>
                      <p className="fs-4 opacity-75">₹{monthlyExemption.toLocaleString('en-IN')} / month</p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Rent & Landlord Details */}
          <Row className="g-4">
            <Col md={6}>
              <Card className="bg-dark text-light border-secondary mb-4">
                <Card.Header>Rent Details</Card.Header>
                <Card.Body>
                  <div className="mb-3">
                    <strong>Monthly Rent Amount:</strong> ₹{monthlyRent.toLocaleString('en-IN')}
                  </div>
                  <div className="mb-3">
                    <strong>Total Rent Paid (Annual):</strong> ₹{totalRentPaid.toLocaleString('en-IN')}
                  </div>
                  <div>
                    <strong>Rental Period:</strong> 12 months
                  </div>
                </Card.Body>
              </Card>

              <Card className="bg-dark text-light border-secondary">
                <Card.Header>Rental Property Address</Card.Header>
                <Card.Body>
                  <p className="mb-0 text-muted">
                    {hraSection.rentalPropertyAddress || "Address not provided"}
                  </p>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <Card className="bg-dark text-light border-secondary mb-4">
                <Card.Header>Landlord Details</Card.Header>
                <Card.Body>
                  <div className="mb-3">
                    <strong>Name:</strong> {landlordDetails.name || "N/A"}
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <strong>PAN:</strong>
                    <span>{landlordDetails.pan || "N/A"}</span>
                    {totalRentPaid > 100000 && !landlordDetails.pan && (
                      <Badge bg="danger">PAN Required (₹1L+)</Badge>
                    )}
                  </div>
                  <div className="mt-3">
                    <strong>Verification Status:</strong> 
                    <Badge bg={hasLandlordPAN ? "success" : "warning"} className="ms-2">
                      {hasLandlordPAN ? "PAN Uploaded" : "PAN Pending"}
                    </Badge>
                  </div>
                </Card.Body>
              </Card>

              <Card className="bg-dark text-light border-secondary">
                <Card.Header>Validation Status</Card.Header>
                <Card.Body>
                  <div className="d-flex flex-wrap gap-3 mb-3">
                    <Badge bg="success" pill>Formula Checked</Badge>
                    {(hraFields.actualHRA?.status || hraFields.actualHRAReceived?.status) === 'verified' && (
                      <Badge bg="success" pill>HRA Verified</Badge>
                    )}
                    {(hraFields.annualRent?.status || hraFields.rentPaid?.status) === 'verified' && (
                      <Badge bg="success" pill>Rent Verified</Badge>
                    )}
                    {(totalRentPaid > 100000 && !hasLandlordPAN) && (
                      <Badge bg="danger" pill>PAN Missing</Badge>
                    )}
                    {!hasRentReceipts && (
                      <Badge bg="warning" pill>Receipts Pending</Badge>
                    )}
                  </div>
                  <div className="small text-muted">
                    Completion: {apiData.reviewStatus?.completionPercentage || hraSection.completionPercentage || 0}%
                    <ProgressBar 
                      now={apiData.reviewStatus?.completionPercentage || hraSection.completionPercentage || 0} 
                      variant="info"
                      className="mt-1"
                    />
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Col>

        {/* Right Column */}
        <Col lg={4}>
          {/* Rent Receipts */}
          <Card className="bg-dark text-light border-secondary mb-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>Rent Receipts</span>
              {rentReceiptUrl && (
                <a href={rentReceiptUrl} target="_blank" rel="noreferrer">
                  <Button size="sm" variant="outline-primary">View Document</Button>
                </a>
              )}
            </Card.Header>
            <Card.Body>
              <div className="text-center mb-4">
                <ProgressBar 
                  now={hasRentReceipts ? 100 : 0} 
                  label={hasRentReceipts ? "Uploaded" : "Pending"} 
                  variant={hasRentReceipts ? "success" : "warning"}
                  style={{ height: "30px", fontSize: "14px" }}
                />
              </div>
              <div className="text-center mb-3">
                {hasRentReceipts ? (
                  <p className="text-success">
                    <i className="bi bi-check-circle me-2"></i>
                    Rent Receipts Available
                  </p>
                ) : (
                  <p className="text-muted">
                    <i className="bi bi-exclamation-circle me-2"></i>
                    No receipts uploaded yet
                  </p>
                )}
              </div>
              
              {/* Document List */}
              <div className="mt-3">
                <h6 className="text-white mb-2">Required Documents:</h6>
                <ul className="list-unstyled">
                  {(hraDocuments.receiptsCoverage || rentReceipts.map((url, index) => ({ name: `Rent Receipt ${index + 1}`, status: "uploaded", url }))).map((doc, index) => (
                    <li key={index} className="mb-2 d-flex justify-content-between align-items-center">
                      <span className="text-gray">{doc.name}</span>
                      <Badge bg={doc.status === 'uploaded' ? 'success' : 'warning'}>
                        {doc.status}
                      </Badge>
                    </li>
                  ))}
                </ul>
              </div>
            </Card.Body>
          </Card>

          {/* Action Buttons */}
          <Card className="bg-dark text-light border-secondary">
            <Card.Header>Review Actions</Card.Header>
            <Card.Body className="d-grid gap-3">
              {reviewActions.canApprove && (
                <Button variant="success" size="lg" onClick={async () => {
                  await executeReviewAction("approve_section", {
                    deductionAmount: annualHRAExemption || leastOfThree,
                    remarks: "HRA exemption approved",
                  });
                }}>
                  Approve HRA
                </Button>
              )}
              
              {reviewActions.canReject && (
                <Button variant="danger" size="lg" onClick={async () => {
                  await executeReviewAction("reject_section", {
                    remarks: "Landlord PAN missing",
                  });
                }}>
                  Reject HRA
                </Button>
              )}
              
              {reviewActions.canComment && (
                <Button variant="outline-info" size="lg" onClick={async () => {
                  const comment = window.prompt("Enter your comment:");
                  if (comment) {
                    await executeReviewAction("comment_section", {
                      remarks: comment,
                    });
                  }
                }}>
                  Add Comment
                </Button>
              )}
              
              <Button variant="outline-secondary" size="lg" onClick={fetchHRAData}>
                Refresh Data
              </Button>

              <Button variant="outline-success" size="lg" onClick={async () => {
                const fileUrl = window.prompt("Enter the public URL for the rent receipt:");
                const monthIndexString = window.prompt("Enter the month index for this receipt (1-12):");
                const monthIndex = monthIndexString ? Number(monthIndexString) : null;
                if (fileUrl && monthIndex) {
                  await executeReviewAction("upload_document", {
                    documentType: "rentReceipt",
                    fileUrl,
                    monthIndex,
                  });
                } else {
                  toast.warning("Receipt upload cancelled or invalid input.");
                }
              }}>
                Upload Rent Receipt
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Hra;
